/**
 * 런타임 설정 로더
 *
 * 정적 호스팅(Cloudflare Pages 등)에 배포한 뒤에도 게이트웨이 주소를 바꿀 수 있도록,
 * 번들에 굽지 않고 배포 산출물 옆의 `config.json`을 부팅 시점에 읽어온다.
 *
 * Cloudflare Quick Tunnel(`*.trycloudflare.com`)은 cloudflared를 재시작할 때마다
 * 주소가 바뀌므로, 주소가 바뀌면 `config.json` 한 파일만 갱신해 재배포하면 된다
 * (tsc + vite 전체 재빌드 불필요).
 *
 * 우선순위: config.json > VITE_* 빌드 환경변수 > window.location 기반 기본값
 * (config.ts의 getGatewayUrl / getMapHttpUrl 참조)
 */

/** config.json의 스키마. 값이 비었거나 키가 없으면 해당 항목은 무시된다. */
export interface RuntimeConfig {
  /** 게이트웨이 WebSocket 주소. 예: wss://foo.trycloudflare.com */
  gatewayUrl?: string;
  /** 맵 통행 데이터 HTTP 주소. 예: https://bar.trycloudflare.com */
  mapHttpUrl?: string;
}

/** 로드 결과 보관소. config.ts의 getter가 매 호출마다 여기를 읽는다. */
let runtimeConfig: RuntimeConfig = {};

/**
 * `config.json`을 읽어 런타임 설정을 채운다.
 *
 * 파일이 없거나 파싱에 실패해도 예외를 던지지 않는다 — 그 경우 기존 동작
 * (빌드 환경변수 또는 location 기반 기본값)으로 조용히 폴백한다. 개발 환경에서는
 * config.json이 없는 것이 정상이다.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // Pages/CDN 캐시에 걸려 옛 주소를 계속 받는 일이 없도록 캐시를 우회한다.
  const url = `${import.meta.env.BASE_URL}config.json?t=${Date.now()}`;

  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
      console.info(`[runtimeConfig] config.json 없음 (HTTP ${res.status}) — 기본값 사용`);
      return runtimeConfig;
    }

    const parsed = (await res.json()) as RuntimeConfig;
    // 빈 문자열은 "설정하지 않음"으로 취급한다 (플레이스홀더 config.json 대응).
    if (parsed.gatewayUrl) runtimeConfig.gatewayUrl = parsed.gatewayUrl;
    if (parsed.mapHttpUrl) runtimeConfig.mapHttpUrl = parsed.mapHttpUrl;

    console.log('[runtimeConfig] 적용됨:', runtimeConfig);
  } catch (err) {
    console.info('[runtimeConfig] config.json 로드 실패 — 기본값 사용', err);
  }

  return runtimeConfig;
}

/** config.ts의 getter 전용 접근자. loadRuntimeConfig() 이전에는 빈 객체다. */
export function getRuntimeConfig(): RuntimeConfig {
  return runtimeConfig;
}
